import { Injectable, inject } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import {
  AdMob,
  AdmobConsentDebugGeography,
  AdmobConsentStatus,
  MaxAdContentRating,
} from '@capacitor-community/admob';
import { environment } from '@storees/environments';
import { FirebaseAnalyticsService } from './firebase/FirebaseAnalyticsService';
import { StoreesSubscriptionService } from './storees-subscription-service';
import { logger } from '../utils/logger';

/**
 * Preloads Google AdMob consent / ATT state at app launch and defers user-facing
 * prompts until a free-tier surface (reverse-trial paywall or story home).
 */
@Injectable({
  providedIn: 'root',
})
export class AdConsentService {
  private firebaseAnalytics = inject(FirebaseAnalyticsService);
  private storeesSubscriptionService = inject(StoreesSubscriptionService);

  private preloadPromise: Promise<void> | null = null;
  private initialized = false;
  private consentStatus: AdmobConsentStatus | string | null = null;
  private consentFormAvailable = false;

  /**
   * Initializes AdMob and loads consent metadata without presenting any UI.
   * Safe to call multiple times; subsequent calls reuse the in-flight promise.
   */
  preload(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return Promise.resolve();
    }

    if (!this.preloadPromise) {
      this.preloadPromise = this.doPreload().finally(() => {
        if (!this.initialized) {
          this.preloadPromise = null;
        }
      });
    }

    return this.preloadPromise;
  }

  /**
   * Shows GDPR consent and/or iOS tracking authorization when the user is on the
   * free tier and has not already responded to those prompts.
   *
   * @returns true when at least one consent dialog was presented.
   */
  async promptIfEligible(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      return false;
    }

    await this.preload();
    if (!this.initialized) {
      return false;
    }

    await this.storeesSubscriptionService.checkEntitlements();
    if (this.storeesSubscriptionService.isSubscribed) {
      return false;
    }

    await this.refreshConsentInfo();
    if (!(await this.needsUserConsentPrompt())) {
      return false;
    }

    return this.showConsentPrompts();
  }

  private async doPreload(): Promise<void> {
    try {
      const admob = environment.admob;
      await AdMob.initialize({
        testingDevices: admob?.testingDevices ?? [],
        initializeForTesting: admob?.isTesting ?? false,
        tagForChildDirectedTreatment: false,
        tagForUnderAgeOfConsent: false,
        maxAdContentRating: MaxAdContentRating.ParentalGuidance,
      });

      await this.firebaseAnalytics.logAdConsentRequested();
      await this.refreshConsentInfo();
      if (this.consentStatus != null) {
        await this.firebaseAnalytics.logAdConsentStatus(String(this.consentStatus));
      }

      this.initialized = true;
      logger.info('AdMob consent metadata preloaded');
    } catch (error) {
      logger.error('AdMob consent preload failed:', error);
    }
  }

  private async refreshConsentInfo(): Promise<void> {
    const consentInfo = await AdMob.requestConsentInfo(
      this.getConsentRequestOptions()
    );
    this.consentStatus = consentInfo.status;
    this.consentFormAvailable = consentInfo.isConsentFormAvailable === true;
  }

  private async needsUserConsentPrompt(): Promise<boolean> {
    if (
      this.consentFormAvailable &&
      this.consentStatus === AdmobConsentStatus.REQUIRED
    ) {
      return true;
    }

    if (Capacitor.getPlatform() === 'ios') {
      const trackingInfo = await AdMob.trackingAuthorizationStatus();
      if (trackingInfo.status === 'notDetermined') {
        return true;
      }
    }

    return false;
  }

  private async showConsentPrompts(): Promise<boolean> {
    let shown = false;

    if (
      this.consentFormAvailable &&
      this.consentStatus === AdmobConsentStatus.REQUIRED
    ) {
      logger.info('Showing deferred AdMob consent form');
      await this.firebaseAnalytics.logAdConsentFormShown();
      const info = await AdMob.showConsentForm();
      this.consentStatus = info.status;
      await this.firebaseAnalytics.logAdConsentStatus(String(info.status));
      shown = true;
    }

    if (Capacitor.getPlatform() === 'ios') {
      const trackingInfo = await AdMob.trackingAuthorizationStatus();
      if (trackingInfo.status === 'notDetermined') {
        logger.info('Requesting deferred iOS tracking authorization');
        await AdMob.requestTrackingAuthorization();
        shown = true;
      }
    }

    return shown;
  }

  private getConsentRequestOptions():
    | {
        debugGeography: AdmobConsentDebugGeography;
        testDeviceIdentifiers: string[];
      }
    | undefined {
    if (environment.production) {
      return undefined;
    }

    return {
      debugGeography: AdmobConsentDebugGeography.EEA,
      testDeviceIdentifiers: environment.admob?.testingDevices ?? [],
    };
  }
}
